import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  Plus,
  Search,
  RefreshCw,
  FileText,
  Video,
  Clock,
  CheckCircle2,
  XCircle,
  Send,
  Eye,
  Loader2,
  Wallet,
  Building2,
  CreditCard,
  Info,
  Upload,
  Download,
  FileSpreadsheet,
  Trash2,
  Link2,
  Copy
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Checkbox } from '../../components/ui/checkbox';

const API = process.env.REACT_APP_BACKEND_URL;
// Public URL for interview links (defaults to API URL if not set)
const PUBLIC_URL = process.env.REACT_APP_PUBLIC_URL || API;

export default function AgentApplications() {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateApplication, setShowCreateApplication] = useState(false);
  const [showCSVImport, setShowCSVImport] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Credit data
  const [personalCredits, setPersonalCredits] = useState(0);
  const [allocatedCredits, setAllocatedCredits] = useState([]);
  const [totalAllocatedCredits, setTotalAllocatedCredits] = useState(0);
  
  // Application form state
  const [creditType, setCreditType] = useState('personal');
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [newApplication, setNewApplication] = useState({
    student_name: '',
    student_email: '',
    student_phone: '',
    university_reference_number: '',
    passport_number: '',
    nic_number: '',
    university_id: '',
    campus: '',
    notes: ''
  });

  // CSV Import state
  const [csvFile, setCsvFile] = useState(null);
  const [csvCreditType, setCsvCreditType] = useState('personal');
  const [csvSelectedOrgId, setCsvSelectedOrgId] = useState('');
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (token) {
      fetchApplications();
      fetchUniversities();
      fetchDashboardData();
    }
  }, [token]);

  // When credit type changes, reset selected org and university
  useEffect(() => {
    setSelectedOrgId('');
    setNewApplication(prev => ({ ...prev, university_id: '' }));
  }, [creditType]);

  // When selected org changes for allocated credits, auto-select the university
  useEffect(() => {
    if (creditType === 'allocated' && selectedOrgId) {
      // Find the organization's allocation info (breakdown uses org_id field)
      const orgAllocation = allocatedCredits.find(alloc => alloc.org_id === selectedOrgId);
      if (orgAllocation) {
        // First try to find matching university by organization_id
        const matchingUni = universities.find(uni => uni.organization_id === selectedOrgId);
        if (matchingUni) {
          setNewApplication(prev => ({ ...prev, university_id: matchingUni.id }));
        } else {
          // Use the org_id itself as the university_id (org IS the university)
          setNewApplication(prev => ({ ...prev, university_id: selectedOrgId }));
        }
      }
    }
  }, [selectedOrgId, creditType, allocatedCredits, universities]);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${API}/api/agent/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPersonalCredits(response.data.personal_credits || 0);
      setTotalAllocatedCredits(response.data.allocated_credits || 0);
      
      // Process allocated credits breakdown
      const breakdown = response.data.allocated_credits_breakdown || [];
      setAllocatedCredits(breakdown);
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/agent/applications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplications(response.data);
    } catch (error) {
      console.error('Error fetching applications:', error);
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const fetchUniversities = async () => {
    try {
      const response = await axios.get(`${API}/api/universities`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUniversities(response.data);
    } catch (error) {
      console.error('Error fetching universities:', error);
    }
  };

  const handleCreateApplication = async (e) => {
    e.preventDefault();
    
    if (!newApplication.student_name || !newApplication.student_email || !newApplication.university_reference_number || !newApplication.university_id) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate credits based on type
    if (creditType === 'personal' && personalCredits <= 0) {
      toast.error('You have no personal credits available. Please purchase more credits or use allocated credits.');
      return;
    }

    if (creditType === 'allocated') {
      if (!selectedOrgId) {
        toast.error('Please select which organization\'s credits to use');
        return;
      }
      const orgCredits = allocatedCredits.find(alloc => alloc.org_id === selectedOrgId);
      // Note: allocated_credits_breakdown returns 'available_credits' not 'credits' or 'amount'
      if (!orgCredits || (orgCredits.available_credits || 0) <= 0) {
        toast.error('Insufficient allocated credits from this organization');
        return;
      }
    }
    
    try {
      setSubmitting(true);
      const payload = {
        ...newApplication,
        credit_type: creditType,
        source_org_id: creditType === 'allocated' ? selectedOrgId : null
      };
      
      const response = await axios.post(`${API}/api/agent/applications`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Application created successfully! Interview link sent to student.');
      setShowCreateApplication(false);
      setNewApplication({
        student_name: '',
        student_email: '',
        student_phone: '',
        university_reference_number: '',
        passport_number: '',
        nic_number: '',
        university_id: '',
        campus: '',
        notes: ''
      });
      setCreditType('personal');
      setSelectedOrgId('');
      fetchApplications();
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create application');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCSVImport = async (e) => {
    e.preventDefault();
    
    if (!csvFile) {
      toast.error('Please select a CSV file');
      return;
    }

    if (csvCreditType === 'allocated' && !csvSelectedOrgId) {
      toast.error('Please select which organization\'s credits to use');
      return;
    }

    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('file', csvFile);
      formData.append('credit_type', csvCreditType);
      if (csvCreditType === 'allocated') {
        formData.append('source_org_id', csvSelectedOrgId);
      }

      const response = await axios.post(`${API}/api/agent/applications/import-csv`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success(`Successfully imported ${response.data.success_count || 0} applications`);
      if (response.data.failed_count > 0) {
        const errorDetails = response.data.errors?.slice(0, 3).join('\n') || '';
        toast.warning(`${response.data.failed_count} applications failed to import${errorDetails ? ': ' + errorDetails : ''}`);
      }
      
      setShowCSVImport(false);
      setCsvFile(null);
      setCsvCreditType('personal');
      setCsvSelectedOrgId('');
      fetchApplications();
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import CSV');
    } finally {
      setImporting(false);
    }
  };

  const downloadCSVTemplate = () => {
    const headers = ['Reference Number', 'Campus', 'Passport Number', 'Student Name', 'Email', 'Phone', 'University'];
    const csvContent = headers.join(',') + '\n' + 'REF-2024-001,Sunderland,AB1234567,John Doe,john@example.com,+44 123 456 7890,University of Sunderland';
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'interviews_mass_import_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const resendInterviewLink = async (applicationId) => {
    try {
      await axios.post(`${API}/api/applications/${applicationId}/resend-link`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Interview link resent to student');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend link');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-600 border-yellow-200"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
      case 'interview_scheduled':
        return <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-200"><Video className="w-3 h-3 mr-1" /> Scheduled</Badge>;
      case 'interview_completed':
        return <Badge variant="outline" className="bg-green-50 text-green-600 border-green-200"><CheckCircle2 className="w-3 h-3 mr-1" /> Completed</Badge>;
      case 'rejected':
        return <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200"><XCircle className="w-3 h-3 mr-1" /> Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const filteredApplications = applications.filter(app =>
    app.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    app.student_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    app.university_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Selection handlers
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(filteredApplications.map(app => app.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id, checked) => {
    if (checked) {
      setSelectedIds(prev => [...prev, id]);
    } else {
      setSelectedIds(prev => prev.filter(i => i !== id));
    }
  };

  const isAllSelected = filteredApplications.length > 0 && selectedIds.length === filteredApplications.length;
  const isSomeSelected = selectedIds.length > 0 && selectedIds.length < filteredApplications.length;

  // Delete selected applications
  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} application(s)?`)) {
      return;
    }

    try {
      await axios.post(`${API}/api/agent/applications/bulk-delete`, 
        { application_ids: selectedIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`${selectedIds.length} application(s) deleted successfully`);
      setSelectedIds([]);
      fetchApplications();
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete applications');
    }
  };

  // Get selected organization info
  const getSelectedOrgInfo = () => {
    if (!selectedOrgId) return null;
    return allocatedCredits.find(alloc => 
      (alloc.org_id || alloc.from_org_id) === selectedOrgId
    );
  };

  // Get available universities based on credit type
  const getAvailableUniversities = () => {
    if (creditType === 'personal') {
      // Personal credits - all universities available
      return universities;
    } else if (creditType === 'allocated' && selectedOrgId) {
      // Allocated credits - only the org's university
      const orgInfo = getSelectedOrgInfo();
      if (orgInfo) {
        // First try to find by organization_id
        const matchedByOrgId = universities.filter(uni => uni.organization_id === selectedOrgId);
        if (matchedByOrgId.length > 0) return matchedByOrgId;
        
        // Otherwise return the org name as a "virtual" university
        return [{
          id: selectedOrgId,
          name: orgInfo.org_name || 'Organization',
          organization_id: selectedOrgId
        }];
      }
    }
    return [];
  };

  const totalCredits = personalCredits + totalAllocatedCredits;

  return (
    <DashboardLayout role="agent">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold">Applications</h1>
            <p className="text-muted-foreground">Manage student applications and interviews</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => setShowCSVImport(true)} data-testid="import-csv-btn">
              <Upload className="w-4 h-4 mr-2" />
              Import CSV
            </Button>
            <Button onClick={() => setShowCreateApplication(true)} className="bg-lime-500 hover:bg-lime-600" data-testid="create-application-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Application
            </Button>
          </div>
        </div>

        {/* Credits Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-lime-100 dark:bg-lime-900/30 rounded-lg">
                  <Wallet className="w-5 h-5 text-lime-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Personal Credits</p>
                  <p className="text-2xl font-bold text-lime-600">{personalCredits}</p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Use for any university</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <Building2 className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Allocated Credits</p>
                  <p className="text-2xl font-bold text-blue-600">{totalAllocatedCredits}</p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                From {allocatedCredits.length} organization{allocatedCredits.length !== 1 ? 's' : ''}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  <CreditCard className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Available</p>
                  <p className="text-2xl font-bold text-purple-600">{totalCredits}</p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Combined credits</p>
            </CardContent>
          </Card>
        </div>

        {/* Allocated Credits Breakdown */}
        {allocatedCredits.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Building2 className="w-4 h-4 text-blue-500" />
                Allocated Credits Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {allocatedCredits.map((alloc, idx) => (
                  <Badge key={idx} variant="outline" className="px-3 py-1.5">
                    <span className="text-muted-foreground mr-2">{alloc.org_name || 'Organization'}:</span>
                    <span className="font-bold text-blue-600">{alloc.available_credits || 0}</span>
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by student name, email, or university..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-applications"
                />
              </div>
              <Button variant="outline" onClick={fetchApplications}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Applications Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-lime-500" />
                  Student Applications
                </CardTitle>
                <CardDescription>
                  {filteredApplications.length} application{filteredApplications.length !== 1 ? 's' : ''} found
                  {selectedIds.length > 0 && ` • ${selectedIds.length} selected`}
                </CardDescription>
              </div>
              {selectedIds.length > 0 && (
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={handleDeleteSelected}
                  data-testid="delete-selected-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete ({selectedIds.length})
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-lime-500" />
              </div>
            ) : filteredApplications.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-20" />
                <p>No applications found</p>
                <Button variant="link" onClick={() => setShowCreateApplication(true)}>
                  Create your first application
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox 
                        checked={isAllSelected}
                        onCheckedChange={handleSelectAll}
                        aria-label="Select all"
                        className={isSomeSelected ? "opacity-50" : ""}
                        data-testid="select-all-checkbox"
                      />
                    </TableHead>
                    <TableHead>Student</TableHead>
                    <TableHead>University</TableHead>
                    <TableHead>Ref Number</TableHead>
                    <TableHead>Passport</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Campus</TableHead>
                    <TableHead>Credit Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredApplications.map((app) => (
                    <TableRow key={app.id} data-testid={`application-row-${app.id}`} className={selectedIds.includes(app.id) ? "bg-slate-50 dark:bg-slate-900/50" : ""}>
                      <TableCell>
                        <Checkbox 
                          checked={selectedIds.includes(app.id)}
                          onCheckedChange={(checked) => handleSelectOne(app.id, checked)}
                          aria-label={`Select ${app.student_name}`}
                          data-testid={`select-checkbox-${app.id}`}
                        />
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{app.student_name || app.name}</p>
                          <p className="text-xs text-muted-foreground">{app.student_email || app.email}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{app.university_name || 'N/A'}</TableCell>
                      <TableCell className="text-sm font-mono">{app.university_reference_number || '-'}</TableCell>
                      <TableCell className="text-sm font-mono">{app.passport_number || '-'}</TableCell>
                      <TableCell className="text-sm">{app.phone || '-'}</TableCell>
                      <TableCell className="text-sm">{app.campus || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={app.credit_type === 'personal' ? 'bg-lime-50 text-lime-600' : 'bg-blue-50 text-blue-600'}>
                          {app.credit_type === 'personal' ? 'Personal' : 'Allocated'}
                        </Badge>
                      </TableCell>
                      <TableCell>{getStatusBadge(app.status)}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {new Date(app.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {app.interview_token && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                const interviewUrl = `${PUBLIC_URL}/interview/${app.interview_token}`;
                                navigator.clipboard.writeText(interviewUrl).then(() => {
                                  toast.success('Interview link copied to clipboard');
                                }).catch(() => {
                                  toast.error('Failed to copy link');
                                });
                              }}
                              title="Copy interview link"
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                          )}
                          {app.interview_id && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => window.open(`/admin/interviews?id=${app.interview_id}`, '_blank')}
                              title="View interview"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => resendInterviewLink(app.id)}
                            title="Resend interview link"
                          >
                            <Send className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Create Application Dialog */}
        <Dialog open={showCreateApplication} onOpenChange={setShowCreateApplication}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Application</DialogTitle>
              <DialogDescription>
                Create an application for a student. An interview link will be sent to their email.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateApplication}>
              <div className="space-y-4 py-4">
                
                {/* Credit Type Selection */}
                <div className="space-y-3 p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg border">
                  <Label className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />
                    Select Credit Type *
                  </Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setCreditType('personal')}
                      className={`p-3 rounded-lg border-2 transition-all text-left ${
                        creditType === 'personal' 
                          ? 'border-lime-500 bg-lime-50 dark:bg-lime-900/30' 
                          : 'border-slate-200 dark:border-slate-700 hover:border-lime-300'
                      }`}
                      data-testid="select-personal-credits"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Wallet className="w-4 h-4 text-lime-600" />
                        <span className="font-medium">Personal</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {personalCredits} credits • Any university
                      </p>
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => setCreditType('allocated')}
                      disabled={totalAllocatedCredits <= 0}
                      className={`p-3 rounded-lg border-2 transition-all text-left ${
                        creditType === 'allocated' 
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30' 
                          : 'border-slate-200 dark:border-slate-700 hover:border-blue-300'
                      } ${totalAllocatedCredits <= 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                      data-testid="select-allocated-credits"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Building2 className="w-4 h-4 text-blue-600" />
                        <span className="font-medium">Allocated</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {totalAllocatedCredits} credits • Org's university only
                      </p>
                    </button>
                  </div>
                  
                  {/* Organization Selection for Allocated Credits */}
                  {creditType === 'allocated' && (
                    <div className="mt-3 space-y-2">
                      {allocatedCredits.length > 0 ? (
                        <>
                          <Label>Select Credit Source *</Label>
                          <Select value={selectedOrgId} onValueChange={setSelectedOrgId}>
                            <SelectTrigger data-testid="select-org">
                              <SelectValue placeholder="Choose which credits to use" />
                        </SelectTrigger>
                        <SelectContent>
                          {allocatedCredits.map((alloc, idx) => (
                            <SelectItem 
                              key={idx} 
                              value={alloc.org_id || `alloc_${idx}`}
                              disabled={(alloc.available_credits || 0) <= 0}
                            >
                              <div className="flex flex-col">
                                <span className="font-medium">{alloc.org_name || 'Organization'}</span>
                                <span className="text-xs text-muted-foreground">
                                  {alloc.available_credits || 0} credits available
                                  {alloc.university_name && ` • ${alloc.university_name}`}
                                </span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      {/* Show selected allocation details */}
                      {selectedOrgId && (
                        <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm">
                          {(() => {
                            const selected = allocatedCredits.find(a => a.org_id === selectedOrgId);
                            if (!selected) return null;
                            return (
                              <div className="space-y-1">
                                <p><strong>Source:</strong> {selected.org_name}</p>
                                <p><strong>Available Credits:</strong> {selected.available_credits}</p>
                                {selected.university_name && (
                                  <p><strong>University:</strong> {selected.university_name}</p>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      )}
                        </>
                      ) : (
                        <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg text-sm text-yellow-700 dark:text-yellow-300">
                          <p>No credit sources found. You have {totalAllocatedCredits} allocated credits but no organization breakdown is available. Please contact support.</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Info Box */}
                {creditType === 'allocated' && selectedOrgId && (
                  <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm text-blue-700 dark:text-blue-300">
                    <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <p>Using allocated credits limits you to creating applications only for this organization's university.</p>
                  </div>
                )}

                {/* Row 1: Full Name & Email */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="student_name">Full Name *</Label>
                    <Input
                      id="student_name"
                      value={newApplication.student_name}
                      onChange={(e) => setNewApplication({ ...newApplication, student_name: e.target.value })}
                      placeholder="John Doe"
                      required
                      data-testid="input-student-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="student_email">Email *</Label>
                    <Input
                      id="student_email"
                      type="email"
                      value={newApplication.student_email}
                      onChange={(e) => setNewApplication({ ...newApplication, student_email: e.target.value })}
                      placeholder="student@email.com"
                      required
                      data-testid="input-student-email"
                    />
                  </div>
                </div>
                
                {/* Row 2: Phone & University Reference Number */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="student_phone">Phone</Label>
                    <Input
                      id="student_phone"
                      value={newApplication.student_phone}
                      onChange={(e) => setNewApplication({ ...newApplication, student_phone: e.target.value })}
                      placeholder="+44 123 456 7890"
                      data-testid="input-student-phone"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="university_reference_number">University Reference Number *</Label>
                    <Input
                      id="university_reference_number"
                      value={newApplication.university_reference_number}
                      onChange={(e) => setNewApplication({ ...newApplication, university_reference_number: e.target.value })}
                      placeholder="REF-2024-001"
                      required
                      data-testid="input-ref-number"
                    />
                  </div>
                </div>

                {/* Row 3: Passport Number & NIC */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="passport_number">Passport Number</Label>
                    <Input
                      id="passport_number"
                      value={newApplication.passport_number}
                      onChange={(e) => setNewApplication({ ...newApplication, passport_number: e.target.value })}
                      placeholder="AB1234567"
                      data-testid="input-passport"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="nic_number">NIC / Government ID</Label>
                    <Input
                      id="nic_number"
                      value={newApplication.nic_number}
                      onChange={(e) => setNewApplication({ ...newApplication, nic_number: e.target.value })}
                      placeholder="12345-6789012-3"
                      data-testid="input-nic"
                    />
                  </div>
                </div>

                {/* Row 4: University & Campus */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="university">University *</Label>
                    {creditType === 'allocated' && selectedOrgId ? (
                      // When using allocated credits, show read-only university
                      <div className="flex items-center gap-3 p-2.5 bg-slate-100 dark:bg-slate-800 rounded-md border text-sm">
                        <Building2 className="w-4 h-4 text-blue-500" />
                        <span className="font-medium">
                          {getAvailableUniversities()[0]?.name || 'Select organization first'}
                        </span>
                      </div>
                    ) : (
                      <Select
                        value={newApplication.university_id}
                        onValueChange={(value) => setNewApplication({ ...newApplication, university_id: value })}
                      >
                        <SelectTrigger data-testid="select-university">
                          <SelectValue placeholder="Select university" />
                        </SelectTrigger>
                        <SelectContent>
                          {universities.map((uni) => (
                            <SelectItem key={uni.id} value={uni.id}>{uni.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="campus">Campus</Label>
                    <Select
                      value={newApplication.campus}
                      onValueChange={(value) => setNewApplication({ ...newApplication, campus: value })}
                    >
                      <SelectTrigger data-testid="select-campus">
                        <SelectValue placeholder="Select campus" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="main">Main Campus</SelectItem>
                        <SelectItem value="city">City Campus</SelectItem>
                        <SelectItem value="london">London Campus</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes (Optional)</Label>
                  <Input
                    id="notes"
                    value={newApplication.notes}
                    onChange={(e) => setNewApplication({ ...newApplication, notes: e.target.value })}
                    placeholder="Any additional notes..."
                    data-testid="input-notes"
                  />
                </div>

                {/* Credit Warning */}
                {creditType === 'personal' && personalCredits <= 0 && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-600 dark:text-red-400">
                    You have no personal credits available. Please purchase more credits or use allocated credits.
                  </div>
                )}
                {creditType === 'allocated' && totalAllocatedCredits <= 0 && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-600 dark:text-red-400">
                    You have no allocated credits. Please contact your organization for more credits.
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowCreateApplication(false)}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-lime-500 hover:bg-lime-600"
                  disabled={submitting || (creditType === 'personal' && personalCredits <= 0) || (creditType === 'allocated' && (totalAllocatedCredits <= 0 || !selectedOrgId))}
                  data-testid="submit-application"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Create Application
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* CSV Import Dialog */}
        <Dialog open={showCSVImport} onOpenChange={setShowCSVImport}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-lime-500" />
                Import Applications from CSV
              </DialogTitle>
              <DialogDescription>
                Upload a CSV file to create multiple applications at once.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCSVImport}>
              <div className="space-y-4 py-4">
                
                {/* Credit Type Selection for CSV */}
                <div className="space-y-3 p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg border">
                  <Label className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />
                    Select Credit Type for All Imports *
                  </Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setCsvCreditType('personal')}
                      className={`p-3 rounded-lg border-2 transition-all text-left ${
                        csvCreditType === 'personal' 
                          ? 'border-lime-500 bg-lime-50 dark:bg-lime-900/30' 
                          : 'border-slate-200 dark:border-slate-700 hover:border-lime-300'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Wallet className="w-4 h-4 text-lime-600" />
                        <span className="font-medium">Personal</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {personalCredits} credits • Any university
                      </p>
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => setCsvCreditType('allocated')}
                      disabled={totalAllocatedCredits <= 0}
                      className={`p-3 rounded-lg border-2 transition-all text-left ${
                        csvCreditType === 'allocated' 
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30' 
                          : 'border-slate-200 dark:border-slate-700 hover:border-blue-300'
                      } ${totalAllocatedCredits <= 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Building2 className="w-4 h-4 text-blue-600" />
                        <span className="font-medium">Allocated</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {totalAllocatedCredits} credits • Org's university only
                      </p>
                    </button>
                  </div>
                  
                  {csvCreditType === 'allocated' && allocatedCredits.length > 0 && (
                    <div className="mt-3 space-y-2">
                      <Label>Select Organization *</Label>
                      <Select value={csvSelectedOrgId} onValueChange={setCsvSelectedOrgId}>
                        <SelectTrigger>
                          <SelectValue placeholder="Choose organization" />
                        </SelectTrigger>
                        <SelectContent>
                          {allocatedCredits.map((alloc, idx) => (
                            <SelectItem 
                              key={idx} 
                              value={alloc.org_id}
                              disabled={(alloc.available_credits || 0) <= 0}
                            >
                              {alloc.org_name || 'Organization'} ({alloc.available_credits || 0} credits)
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {/* Download Template */}
                <Button type="button" variant="outline" onClick={downloadCSVTemplate} className="w-full">
                  <Download className="w-4 h-4 mr-2" />
                  Download CSV Template
                </Button>

                {/* File Upload */}
                <div className="space-y-2">
                  <Label>CSV/XLSX File *</Label>
                  <Input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                    className="cursor-pointer"
                  />
                  {csvFile && (
                    <p className="text-sm text-muted-foreground">
                      Selected: {csvFile.name}
                    </p>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowCSVImport(false)}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-lime-500 hover:bg-lime-600"
                  disabled={importing || !csvFile || (csvCreditType === 'allocated' && !csvSelectedOrgId)}
                >
                  {importing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Import Applications
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
